// Persistent "the user wanted to check out" intent.
//
// Affiliate / marketing links carry `?promo=…&checkout=true`, but the multi-hop
// login forwards (login → Cognito → auth-redirect → upgrade → home) and the
// social OAuth round-trip routinely strip the query string before the user
// finally lands. Relying on the URL is hopeless, so we stash the intent the
// moment we see it and re-apply it once the user lands authenticated.
//
// Belt-and-braces: write to localStorage, sessionStorage AND a short-lived
// cookie, and read from whichever survived. Every access is wrapped — storage
// can throw in private-mode / locked-down browsers.

const KEY = "ortex_pending_checkout";
const COOKIE_MAX_AGE_SECONDS = 30 * 60; // short-lived: long enough for the auth flow

export type CheckoutIntent = { promo?: string; t?: string; v?: string };

const writeCookie = (value: string): void => {
    try {
        document.cookie = `${KEY}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE_SECONDS};SameSite=Lax`;
    } catch {
        /* cookies disabled — other stores cover us */
    }
};

const readCookie = (): string | null => {
    try {
        const match = document.cookie.match(new RegExp(`(?:^|; )${KEY}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
    } catch {
        return null;
    }
};

const clearCookie = (): void => {
    try {
        document.cookie = `${KEY}=;path=/;max-age=0;SameSite=Lax`;
    } catch {
        /* no-op */
    }
};

/** Persist the checkout intent across all three stores. Safe to call repeatedly. */
export const saveCheckoutIntent = (intent: CheckoutIntent): void => {
    if (typeof window === "undefined") return;
    const value = JSON.stringify({ promo: intent.promo || "", t: intent.t || "", v: intent.v || "" });
    try {
        localStorage.setItem(KEY, value);
    } catch {
        /* no-op */
    }
    try {
        sessionStorage.setItem(KEY, value);
    } catch {
        /* no-op */
    }
    writeCookie(value);
};

/** Read the checkout intent from whichever store survived, or null. */
export const loadCheckoutIntent = (): CheckoutIntent | null => {
    if (typeof window === "undefined") return null;
    let raw: string | null = null;
    try {
        raw = localStorage.getItem(KEY);
    } catch {
        /* no-op */
    }
    if (!raw) {
        try {
            raw = sessionStorage.getItem(KEY);
        } catch {
            /* no-op */
        }
    }
    if (!raw) raw = readCookie();
    if (!raw) return null;
    try {
        return JSON.parse(raw) as CheckoutIntent;
    } catch {
        return null;
    }
};

/**
 * Resolve the effective checkout parameters: an explicit URL query wins, the
 * persisted intent fills the gaps. This lets the plan preselection (t/v) and
 * promo survive flows that strip the query string — signup via /register, the
 * /upgrade hop after account creation, and social OAuth round-trips — so a
 * shared link like ?promo=TRYAI&t=monthly&v=advanced still lands the new user
 * on the intended plan/interval after signing up.
 */
export const effectiveCheckoutParams = (query: Record<string, unknown> | undefined): CheckoutIntent => {
    const intent = loadCheckoutIntent();
    const fromQuery = (key: "promo" | "t" | "v"): string | undefined => {
        const val = query?.[key];
        return typeof val === "string" && val !== "" && val !== "true" ? val : undefined;
    };
    return {
        promo: fromQuery("promo") || intent?.promo || undefined,
        t: fromQuery("t") || intent?.t || undefined,
        v: fromQuery("v") || intent?.v || undefined,
    };
};

/** Wipe the checkout intent from all three stores (e.g. once the user has paid). */
export const clearCheckoutIntent = (): void => {
    if (typeof window === "undefined") return;
    try {
        localStorage.removeItem(KEY);
    } catch {
        /* no-op */
    }
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* no-op */
    }
    clearCookie();
};

/**
 * The URL that re-opens checkout on the page the user is on: `asPath` (the real path, never the
 * route template) with the intent's params and `checkout=true` added to whatever query it already
 * has. Building it from `router.pathname` sent stock pages to the literal `/s/[exchange]/[ticker]`
 * (ortex-gui#1305).
 */
export const checkoutReplayHref = (asPath: string, intent: CheckoutIntent): string => {
    const hashAt = asPath.indexOf("#");
    const hash = hashAt >= 0 ? asPath.slice(hashAt) : "";
    const beforeHash = hashAt >= 0 ? asPath.slice(0, hashAt) : asPath;
    const queryAt = beforeHash.indexOf("?");
    const path = queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash;
    const params = new URLSearchParams(queryAt >= 0 ? beforeHash.slice(queryAt + 1) : "");
    if (intent.promo) params.set("promo", intent.promo);
    if (intent.t) params.set("t", intent.t);
    if (intent.v) params.set("v", intent.v);
    params.set("checkout", "true");
    return `${path}?${params.toString()}${hash}`;
};
